import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Layout from "../components/Layout";
import MetaTags from "../lib/MetaTags";
import Breadcrumbs from "../components/Breadcrumbs";
import { ArticleCard, categoryName } from "../components/ArticleCard";
import { fetchArticleBySlug } from "../lib/api";
import { SITE } from "../lib/categories";
import { Clock, Calendar, User, Share2, ArrowLeft } from "lucide-react";

export default function Article() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setData(null);
    fetchArticleBySlug(slug)
      .then(setData)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <div className="ceg-container py-16">
          <p className="text-sm text-[#5C5C5C]" data-testid="article-loading">Loading...</p>
        </div>
      </Layout>
    );
  }

  if (notFound || !data?.article) {
    return (
      <Layout>
        <div className="ceg-container py-24 text-center">
          <p className="ceg-overline">404</p>
          <h1 className="font-['Outfit'] font-black text-5xl tracking-tighter mt-3">Article not found</h1>
          <Link to="/" className="ceg-btn-primary mt-6 inline-flex" data-testid="article-back-home"><ArrowLeft size={14} className="mr-1" /> Back home</Link>
        </div>
      </Layout>
    );
  }

  const { article, related } = data;
  const publishedDate = article.published_at ? new Date(article.published_at) : null;
  const publishedReadable = publishedDate ? publishedDate.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";
  const cat = categoryName(article.category);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.meta_description || article.excerpt,
    image: article.hero_image,
    author: { "@type": "Person", name: article.author || "Editorial Team" },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: {
        "@type": "ImageObject",
        url: `${SITE.url}/logo.png`,
      },
    },
    datePublished: article.published_at,
    dateModified: article.updated_at,
    mainEntityOfPage: `${SITE.url}/article/${article.slug}`,
  };

  const faqSchema = (article.faqs && article.faqs.length > 0) && {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: article.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
      { "@type": "ListItem", position: 2, name: cat, item: `${SITE.url}/category/${article.category}` },
      { "@type": "ListItem", position: 3, name: article.title, item: `${SITE.url}/article/${article.slug}` },
    ],
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: article.title, url: window.location.href });
      } else {
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch {}
  };

  return (
    <Layout>
      <MetaTags
        title={article.title}
        description={article.meta_description || article.excerpt}
        image={article.hero_image}
        type="article"
        schema={[schema, breadcrumbSchema, faqSchema]}
      />

      <article>
        {/* Hero */}
        <section className="bg-white border-b border-[#121212]">
          <div className="ceg-container py-8">
            <Breadcrumbs
              items={[
                { to: "/", label: "Home" },
                { to: `/category/${article.category}`, label: cat },
                { label: article.title },
              ]}
            />
            <p className="ceg-overline mt-6">{cat}</p>
            <h1 className="font-['Outfit'] font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05] max-w-4xl break-words">
              {article.title}
            </h1>
            <p className="mt-5 text-base sm:text-lg md:text-xl text-[#5C5C5C] max-w-3xl leading-relaxed">{article.excerpt}</p>
            <div className="mt-6 flex flex-wrap items-center gap-5 text-xs uppercase tracking-wider text-[#5C5C5C]" data-testid="article-meta">
              <span className="flex items-center gap-1"><User size={12} /> {article.author}</span>
              {publishedReadable && <span className="flex items-center gap-1"><Calendar size={12} /> {publishedReadable}</span>}
              <span className="flex items-center gap-1"><Clock size={12} /> {article.read_time} min read</span>
              <button onClick={share} className="flex items-center gap-1 hover:text-[#0055FF]" data-testid="article-share">
                <Share2 size={12} /> Share
              </button>
            </div>
          </div>
          {article.hero_image && (
            <div className="ceg-container pb-8">
              <div className="aspect-[16/8] overflow-hidden border border-[#121212]">
                <img src={article.hero_image} alt={article.title} className="w-full h-full object-cover" />
              </div>
            </div>
          )}
        </section>

        {/* Body */}
        <section className="ceg-container py-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-8 article-prose" data-testid="article-content">
            <ReactMarkdown>{article.content}</ReactMarkdown>

            {/* FAQ */}
            {article.faqs?.length > 0 && (
              <div className="mt-12">
                <h2>Frequently asked questions</h2>
                <div className="not-prose space-y-3" data-testid="article-faqs">
                  {article.faqs.map((f, i) => (
                    <details key={i} className="border border-[#121212] bg-white p-4" data-testid={`faq-${i}`}>
                      <summary className="font-['Outfit'] font-bold cursor-pointer text-lg tracking-tight">{f.question}</summary>
                      <p className="mt-3 text-[#1f1f1f] leading-relaxed">{f.answer}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {article.tags?.length > 0 && (
              <div className="mt-12 border-t border-[#E5E5E5] pt-6 not-prose">
                <p className="ceg-overline mb-3">Tags</p>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((t) => (
                    <Link key={t} to={`/tag/${t}`} className="text-xs uppercase tracking-wider px-3 py-1 border border-[#121212] hover:bg-[#121212] hover:text-white transition-colors" data-testid={`tag-${t}`}>
                      #{t}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="col-span-12 md:col-span-4 space-y-6">
            <div className="bg-[#121212] text-white p-6">
              <p className="ceg-overline text-white">Editorial</p>
              <h3 className="font-['Outfit'] font-black text-2xl tracking-tighter mt-2">No paid placements.</h3>
              <p className="text-sm text-white/70 mt-3">Every recommendation in this article is independent. We don't accept payment for coverage.</p>
              <Link to="/about" className="mt-3 inline-block text-xs uppercase tracking-wider underline underline-offset-4 decoration-[#FF3823]">Our editorial standards →</Link>
            </div>
          </aside>
        </section>

        {/* Related */}
        {related?.length > 0 && (
          <section className="bg-white border-t border-[#121212]">
            <div className="ceg-container py-12">
              <p className="ceg-overline">Keep reading</p>
              <h2 className="font-['Outfit'] font-black tracking-tighter text-3xl md:text-4xl mt-2 mb-8">Related guides</h2>
              <div className="grid grid-cols-12 gap-6">
                {related.map((a, i) => (
                  <div key={a.slug} className="col-span-12 sm:col-span-6 lg:col-span-3">
                    <ArticleCard article={a} index={i} size="sm" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </Layout>
  );
}
